import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { CampData } from '../types/camp'
import type {
  Testimonial,
  FAQ,
  ScheduleItem,
  HeroMember,
  Activity,
  SiteImages,
  TransportCity,
  GalleryPhoto,
} from '../types/wp'
import { fetchBootstrap } from '../services/api'

export interface BootstrapData {
  camps: CampData[]
  testimonials: Testimonial[]
  faqs: FAQ[]
  schedule: ScheduleItem[]
  heroes: HeroMember[]
  activities: Activity[]
  siteImages: SiteImages | null
  transportCities: TransportCity[]
  gallery: GalleryPhoto[]
}

const EMPTY: BootstrapData = {
  camps: [],
  testimonials: [],
  faqs: [],
  schedule: [],
  heroes: [],
  activities: [],
  siteImages: null,
  transportCities: [],
  gallery: [],
}

interface BootstrapContextValue {
  data: BootstrapData
  loading: boolean
  /** True when BOTH the bootstrap call AND the static camps.json fallback failed. */
  error: boolean
}

const BootstrapContext = createContext<BootstrapContextValue>({
  data: EMPTY,
  loading: true,
  error: false,
})

/**
 * Fetches all CMS content in a single request once, on app mount, and shares it
 * with every hook below. Avoids the previous pattern where each section fired
 * its own REST call (8 cold WordPress bootstraps per page load).
 */
export function BootstrapProvider({
  children,
  initialData,
}: {
  children: ReactNode
  /** Build-time seed used by the prerenderer so static HTML has real CMS content.
      Never passed on the client, which always fetches fresh on mount. */
  initialData?: BootstrapData
}) {
  const [data, setData] = useState<BootstrapData>(initialData ?? EMPTY)
  const [loading, setLoading] = useState(!initialData)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const res = await fetchBootstrap()
        if (cancelled) return
        setData({
          camps: res.camps ?? [],
          testimonials: res.testimonials ?? [],
          faqs: res.faqs ?? [],
          schedule: res.schedule ?? [],
          heroes: [...(res.heroes ?? [])].sort((a, b) => a.order - b.order),
          activities: [...(res.activities ?? [])].sort((a, b) => a.order - b.order),
          siteImages: res.siteImages ?? null,
          transportCities: [...(res.transportCities ?? [])].sort((a, b) => a.order - b.order),
          gallery: res.gallery ?? [],
        })
      } catch {
        // Fallback: static camps JSON for dev/preview without WordPress.
        try {
          const file = await (await fetch('/data/camps.json')).json()
          if (cancelled) return
          setData({
            ...EMPTY,
            camps: (file.camps ?? []).filter((c: CampData) => c.active),
          })
        } catch {
          // Both the live bootstrap and the static fallback failed.
          if (!cancelled) {
            setData(EMPTY)
            setError(true)
          }
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <BootstrapContext.Provider value={{ data, loading, error }}>{children}</BootstrapContext.Provider>
  )
}

export function useBootstrap() {
  return useContext(BootstrapContext)
}
